/* eslint-disable complexity */
import { record } from '../utils/recorder';
import { cloneDeep } from '../utils/clone-deep';
import { SetDiagramDrawingMutation } from '../mutation/shape/smartArt/set-diagram-drawing-mutation';
import { GraphicFrameBaseMutationAPI } from './graphic-frame-base-mutation-api';
import type { SmartArtDataModel } from '../model/smart-art-data-model';
import type { ISmartArtData, ScaleTransformParams } from '../data';
import type { DeltaOf } from '../utils/delta';
import type {
    ICTPositiveSize2D,
    ICTShapeMsDsp,
    ICTTextParagraph,
    ICTPoint2D,
    IEGTextRun,
} from '@tencent/slide-sdk-pb-model';

export class SmartArtMutationAPI extends GraphicFrameBaseMutationAPI<ISmartArtData, SmartArtDataModel> {
    /**
     * 更新 GraphicalObjectFrame 属性
     */
    @record()
    public updateProps({ spPr, nvPr, graphic, bwMode }: DeltaOf<ISmartArtData>) {
        if (spPr?.xfrm) {
            this.computeChildTransform({ transform: spPr?.xfrm });
        }
        return super.updateProps({ spPr, nvPr, graphic, bwMode });
    }
    /**
     * 重新计算 SmartArt 中 drawing 的位置。
     * 如果位置或比例发生变动，更新 SmartArt 中各子形状的变换。
     *
     * @param params - 包含变换参数的对象
     * @param params.transform - 描述二维变换的增量，可选择传递
     * @param params.scaleX - 水平方向的缩放比例，可选择传递
     * @param params.scaleY - 垂直方向的缩放比例，可选择传递
     */
    public computeChildTransform(params: ScaleTransformParams) {
        const { transform, scaleX, scaleY } = params;
        // 缺省情况下缩放比例设为1
        let finalScaleX = scaleX !== undefined ? scaleX : 1;
        let finalScaleY = scaleY !== undefined ? scaleY : 1;
        // 如果传入了transform，根据其计算缩放比例
        if (transform) {
            const originXfrm = this.model.store?.data?.spPr?.xfrm;

            if (originXfrm?.ext?.cx !== undefined && transform.ext?.cx !== null && transform.ext?.cx !== undefined) {
                finalScaleX = transform.ext.cx / originXfrm.ext.cx;
            }
            if (originXfrm?.ext?.cy !== undefined && transform.ext?.cy !== null && transform.ext?.cy !== undefined) {
                finalScaleY = transform.ext.cy / originXfrm.ext.cy;
            }
        }

        if (finalScaleX === 1 && finalScaleY === 1) return;

        const drawing = this.model.store.data?.diagram?.drawing;
        if (!drawing) return;
        const spLst = cloneDeep(drawing.spTree?.egGroupShapeMsDsp);
        // 修改子元素位置及宽高
        if (spLst && spLst.length > 0) {
            const scaleTransform = (value: number | undefined, scale: number) => Math.floor((value || 0) * scale) || 0;
            const transformOffExt = (off: ICTPoint2D, ext: ICTPositiveSize2D, scaleX: number, scaleY: number) => ({
                off: {
                    x: scaleTransform(off?.x, scaleX),
                    y: scaleTransform(off?.y, scaleY),
                },
                ext: {
                    cx: scaleTransform(ext?.cx, scaleX),
                    cy: scaleTransform(ext?.cy, scaleY),
                },
            });
            // 等比缩放修改文字大小
            const scaleTextProperties = (textProperties: any, scale: number) => {
                if (!textProperties) return textProperties;
                const scaledProperties = { ...textProperties };

                if (scaledProperties.sz !== undefined) {
                    scaledProperties.sz = Math.floor(scaledProperties.sz * scale);
                }

                return scaledProperties;
            };
            const transformParagraphs = (paragraphs: ICTTextParagraph[] | undefined, scale: number) => {
                if (!paragraphs || paragraphs.length === 0) return paragraphs;

                return paragraphs.map((paragraph) => {
                    const transformedParagraph = { ...paragraph };
                    if (transformedParagraph.pPr) {
                        transformedParagraph.pPr = scaleTextProperties(transformedParagraph.pPr, scale);
                    }
                    if (transformedParagraph.egTextRun) {
                        transformedParagraph.egTextRun = transformedParagraph.egTextRun.map((textRun: IEGTextRun) => {
                            if (textRun?.r && textRun?.r?.rPr) {
                                textRun.r.rPr = scaleTextProperties(textRun.r.rPr, scale);
                            }
                            return textRun;
                        });
                    }
                    return transformedParagraph;
                });
            };

            spLst.forEach((shape: { sp: ICTShapeMsDsp }, index: number) => {
                const { spPr, txXfrm, txBody } = shape.sp;

                const getTransformedShapeProperties = (properties: any, scaleX: number, scaleY: number) => {
                    if (!properties?.xfrm) return properties;
                    const { off, ext, ...rest } = properties.xfrm;
                    return {
                        ...properties,
                        xfrm: {
                            ...rest,
                            ...transformOffExt(off, ext, scaleX, scaleY),
                        },
                    };
                };

                const getTransformedTextProperties = (properties: any, scaleX: number, scaleY: number) => {
                    if (!properties) return properties;
                    const { off, ext, ...rest } = properties;
                    return {
                        ...rest,
                        ...transformOffExt(off, ext, scaleX, scaleY),
                    };
                };

                spLst[index] = {
                    sp: {
                        ...shape.sp,
                        spPr: getTransformedShapeProperties(spPr, finalScaleX, finalScaleY),
                        txXfrm: getTransformedTextProperties(txXfrm, finalScaleX, finalScaleY),
                        txBody: txBody
                            ? {
                                  ...txBody,
                                  p: transformParagraphs(txBody.p, (finalScaleX + finalScaleY) / 2), // 使用均值进行缩放
                              }
                            : undefined,
                    },
                };
            });
        }
        const newDrawing = {
            ...drawing,
            spTree: {
                ...drawing.spTree,
                egGroupShapeMsDsp: spLst,
            },
        };
        return this.transaction(() => {
            this.request([
                new SetDiagramDrawingMutation({
                    shapeId: this.model.id,
                    pageId: this.model.getPage().id,
                    drawing: newDrawing,
                }),
            ]);
        });
    }
}
